import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEntityForm } from '@/shared/lib/useEntityForm';
import { FileUploadField } from '@/shared/ui/FileUploadField';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { ModalDialog } from '@/shared/ui/ModalDialog';
import { TextField } from '@/shared/ui/TextField';
import {
  EMPTY_SLIDE,
  SLIDE_TYPE_ACTION,
  deleteSlideImage,
  fetchUnattachedSlideImage,
  saveSlide,
  slideToValues,
  uploadSlideImage,
  type Slide,
  type SlideValues,
} from './api';

export const SLIDES_KEY = 'publications-story-slides';

const TYPE_OPTIONS = [
  { value: 'static', label: 'Статичная' },
  { value: SLIDE_TYPE_ACTION, label: 'С призывом к действию' },
];
const ORIENTATION_OPTIONS = [
  { value: 'horizontal', label: 'Горизонтальная' },
  { value: 'vertical', label: 'Вертикальная' },
];
const BUTTON_TYPE_OPTIONS = [
  { value: 'button', label: 'Кнопка' },
  { value: '', label: 'Нет кнопки' },
];
const BUTTON_STYLE_OPTIONS = [
  { value: 'primary', label: 'primary' },
  { value: 'reversedPrimary', label: 'reversedPrimary' },
];
const SELECT_WIDTH = 250;

type FormProps = { previewId: number; slide?: Slide; initial: SlideValues; onClose: () => void };

const SlideForm = ({ previewId, slide, initial, onClose }: FormProps) => {
  const qc = useQueryClient();
  const form = useEntityForm(
    initial,
    (values) => saveSlide(previewId, slide?.id, values),
    () => {
      void qc.invalidateQueries({ queryKey: [SLIDES_KEY] });
      onClose();
    },
  );
  const { values, errors, setField } = form;

  return (
    <ModalDialog
      title={slide ? 'Обновить сторис' : 'Создать сторис'}
      onClose={onClose}
      isSaving={form.isSaving}
      onSave={form.save}
    >
      <TextField
        label="Имя сторис"
        placeholder="Имя..."
        value={values.name}
        errors={errors.name}
        onChange={(v) => setField('name', v)}
      />
      <FormField label="Тип сторис" errors={errors.type}>
        <FilterSelect
          options={TYPE_OPTIONS}
          value={values.type}
          isClearable={false}
          width={SELECT_WIDTH}
          onChange={(v) => setField('type', v)}
        />
      </FormField>
      <TextField
        label="Градиент №1"
        placeholder="Код цвета 1..."
        value={values.gradientFirst}
        errors={errors.gradient_colors_1}
        onChange={(v) => setField('gradientFirst', v, 'gradient_colors_1')}
      />
      <TextField
        label="Градиент №2"
        placeholder="Код цвета 2..."
        value={values.gradientSecond}
        errors={errors.gradient_colors_2}
        onChange={(v) => setField('gradientSecond', v, 'gradient_colors_2')}
      />
      <FormField label="Ориентация градиента" errors={errors.orientation}>
        <FilterSelect
          options={ORIENTATION_OPTIONS}
          value={values.orientation}
          isClearable={false}
          width={SELECT_WIDTH}
          onChange={(v) => setField('orientation', v)}
        />
      </FormField>
      <FileUploadField
        label="Фон сторис"
        files={values.image}
        errors={errors.image_id}
        previewWidth={140}
        upload={uploadSlideImage}
        remove={deleteSlideImage}
        onChange={(files) => setField('image', files, 'image_id')}
      />
      {values.type === SLIDE_TYPE_ACTION && (
        <>
          <TextField
            label="Название кнопки"
            placeholder="Текст кнопки..."
            value={values.textButton}
            errors={errors.text_button}
            onChange={(v) => setField('textButton', v, 'text_button')}
          />
          <FormField label="Тип кнопки" errors={errors.type_button}>
            <FilterSelect
              options={BUTTON_TYPE_OPTIONS}
              value={values.typeButton}
              isClearable={false}
              width={SELECT_WIDTH}
              onChange={(v) => setField('typeButton', v, 'type_button')}
            />
          </FormField>
          <TextField
            label="Ссылка на кнопке"
            placeholder="URL кнопки..."
            value={values.uriButton}
            errors={errors.uri_button}
            onChange={(v) => setField('uriButton', v, 'uri_button')}
          />
          <FormField label="Стиль кнопки" errors={errors.style_button}>
            <FilterSelect
              options={BUTTON_STYLE_OPTIONS}
              value={values.styleButton}
              isClearable={false}
              width={SELECT_WIDTH}
              onChange={(v) => setField('styleButton', v, 'style_button')}
            />
          </FormField>
        </>
      )}
      {errors.common && <p className="text-red">{errors.common[0]}</p>}
    </ModalDialog>
  );
};

/** New slide: preloads the image the API already holds for it (legacy behaviour). */
const CreateSlideForm = ({ previewId, onClose }: Omit<FormProps, 'slide' | 'initial'>) => {
  const { data: image, isLoading } = useQuery({
    queryKey: ['publications-story-unattached-image'],
    queryFn: fetchUnattachedSlideImage,
    gcTime: 0,
  });
  if (isLoading) return null;
  return (
    <SlideForm
      previewId={previewId}
      initial={{ ...EMPTY_SLIDE, image: image ? [image] : [] }}
      onClose={onClose}
    />
  );
};

type ButtonProps = { previewId: number; slide?: Slide };

/** "Создать" (header) / "Обновить" (row) button opening the slide modal. */
export const SlideModalButton = ({ previewId, slide }: ButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const close = () => setIsOpen(false);

  return (
    <>
      <button
        type="button"
        className={slide ? 'btn btn-success btn-sm' : 'btn btn-info pull-right'}
        onClick={() => setIsOpen(true)}
      >
        {slide ? 'Обновить' : 'Создать'}
      </button>
      {isOpen &&
        (slide ? (
          <SlideForm previewId={previewId} slide={slide} initial={slideToValues(slide)} onClose={close} />
        ) : (
          <CreateSlideForm previewId={previewId} onClose={close} />
        ))}
    </>
  );
};
